import React, {useEffect, useRef, useState} from 'react';
import { connect } from 'react-redux';
import WMSCapabilities from 'wms-capabilities';
import LayerControlIcon from '../../../../assets/icons/geo-node-layers.svg';
import {Collapse, Drawer, Spin, Popover} from "antd";
import L from 'leaflet';
import {CloseOutlined, InfoCircleTwoTone} from '@ant-design/icons';
import TopSection from "../SideNav/components/NavItemDetails/components/TopSection";
import CustomSearch from "../SideNav/components/NavItemDetails/components/CustomSearch";
import LayerCategory from "./components/LayerCategory";
import API from "../../../../API";
import {useMap} from "react-leaflet";
import PropTypes from "prop-types";
import {mapDataSetsActions, mapDataSetsSelectors} from "../../../../redux/modules/map/dataSets";
import {bindActionCreators} from "redux";
import './styles.css';
import DecimalStep from "../../../components/DecimalStep";
// Reuse existing item/action styling
import './components/LayerCategory/styles.css';
import './components/LayerCategory/componets/LayerItem/components/LayerItemActions/styles.css';
// Import custom hooks
import useMapInteractionLock from './hooks/useMapInteractionLock';
import useResponsiveBreakpoint from './hooks/useResponsiveBreakpoint';

const {Panel} = Collapse;


const LayerControl = ({ addedDataSet, removedDataSet, removeDataLayer, addDataLayer, localLayers }) => {
    const [mapLayers, setMapLayers] = useState({});
    const [showSideNav, setShowSideNav] = useState(false);
    const [capabilities, setCapabilities] = useState({});
    const [layerCategories, setLayerCategories] = useState([]);
    const ref = useRef();
    const drawerBodyRef = useRef(); // Ref for event propagation blocking
    const map = useMap();
    
    // Custom hooks for enhanced functionality
    const { isMobile, isMd } = useResponsiveBreakpoint();
    
    // Lock map interactions when drawer is open
    useMapInteractionLock(map, showSideNav);

    // Local actions UI for Project Layers, following the same pattern as LayerItemActions
    const ProjectLayerItemActions = ({ layer }) => {
        const [close, setClose] = useState(!!layer.checked);
        useEffect(() => setClose(!!layer.checked), [layer.checked]);
        return (
            <div className='LayerItemActions' data-testid={`project-layer-actions-${layer.id}`}>
                <div
                    data-testid={`project-layer-add-${layer.id}`}
                    className={`add${close ? ' is-hidden' : ''}`}
                    title='Add layer on map'
                    onClick={() => { setClose(true); layer.onChange && layer.onChange(true); }}
                >
                    Add
                </div>
                <div
                    data-testid={`project-layer-remove-${layer.id}`}
                    className={`close${close ? '' : ' is-hidden'}`}
                    onClick={() => { setClose(false); layer.onChange && layer.onChange(false); }}
                >
                    <CloseOutlined/>
                </div>
            </div>
        );
    };

    const ProjectLayerItemDetails = ({ layer }) => {
        const { label, details } = layer || {};
        return (
            <div className='DataSetTitleHoverInfo'>
                <div>Name: {label}</div>
                {details?.abstract && <div>Abstract: {details.abstract}</div>}
                {details?.data_quality_statement && <div>Data Quality Statement: {details.data_quality_statement}</div>}
                {details?.supplemental_information && <div>Supplemental Information: {details.supplemental_information}</div>}
            </div>
        );
    };

    const ProjectLayerItemHeader = ({ layer }) => {
        const rowRef = useRef();
        return (
            <div className='DataSet' ref={rowRef} data-testid={`project-layer-${layer.id}`}>
                <Popover
                    className='data-set-info'
                    content={<ProjectLayerItemDetails layer={layer} />}
                    title={<b>Data Set Details</b>}
                    getPopupContainer={() => rowRef.current}
                    placement="top"
                >
                    <InfoCircleTwoTone twoToneColor="#0f6788"/>
                </Popover>
                <div className='data-set-name-source'>
                    <div title={layer.label} className="dataset-title">{layer.label}</div>
                </div>
                <ProjectLayerItemActions layer={layer} />
            </div>
        );
    };
    // Set up event propagation blocking when drawer body is available
    useEffect(() => {
        if (drawerBodyRef.current && showSideNav) {
            const drawerEl = drawerBodyRef.current;
            
            // Use Leaflet's DomEvent utilities to block scroll propagation only
            L.DomEvent.disableScrollPropagation(drawerEl);
            
            // Block only wheel events from reaching the map, allow clicks to work within drawer
            const stopWheelPropagation = (e) => {
                e.stopPropagation();
            };
            
            drawerEl.addEventListener('wheel', stopWheelPropagation);
            drawerEl.addEventListener('touchmove', stopWheelPropagation);
            
            return () => {
                drawerEl.removeEventListener('wheel', stopWheelPropagation);
                drawerEl.removeEventListener('touchmove', stopWheelPropagation);
            };
        }
    }, [showSideNav]);

    useEffect(() => {
        API.getCapabilities()
            .then( res => setCapabilities(new WMSCapabilities().parse(res)));

        API.getLayersCategories()
            .then(({objects}) => {
                const data = objects.filter(({count}) => count > 0);
                setLayerCategories(data);
            });

        return () => {
            removeDataLayer(null);
            addDataLayer(null);
        }
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps


    const changeOpacity = (value, layer) => {
        // Add defensive guard
        if (mapLayers[layer.typename]) {
            mapLayers[layer.typename].setOpacity(value);
        }
    };

    const addDataSet = (dataSet) => {
        // Add defensive guard for capabilities
        if (!capabilities?.Capability?.Layer?.Layer) {
            console.warn('Capabilities not yet loaded, cannot add dataset');
            return;
        }
        
        const myLayer = capabilities.Capability.Layer.Layer.find(l => l.Name === dataSet.typename );
        if (!myLayer) {
            console.warn(`Layer ${dataSet.typename} not found in capabilities`);
            return;
        }
        
        const {LatLonBoundingBox} = myLayer;

        const geonodeLayer = L.tileLayer.wms(`${process.env.REACT_APP_GEONODE_URL}/geoserver/ows`, {
            layers: dataSet.typename,
            format: 'image/png',
            transparent: true,
        });

        // Use immutable update instead of mutation
        setMapLayers(prev => ({ ...prev, [dataSet.typename]: geonodeLayer }));
        map.addLayer(geonodeLayer);
        
        if (LatLonBoundingBox) {
            const corner1 = L.latLng(LatLonBoundingBox[1],LatLonBoundingBox[0]);
            const corner2 = L.latLng(LatLonBoundingBox[3],LatLonBoundingBox[2]);
            const bounds = L.latLngBounds(corner1, corner2);
            map.fitBounds(bounds);
        }
    }


    const removeDataSet = (dataSet) => {
        // Add defensive guard
        if (mapLayers[dataSet.typename]) {
            map.removeLayer(mapLayers[dataSet.typename]);
            // Use immutable update instead of mutation
            setMapLayers(prev => {
                const next = { ...prev };
                delete next[dataSet.typename];
                return next;
            });
        }
    }


    useEffect(() => {
        if(addedDataSet) addDataSet(addedDataSet);
    }, [addedDataSet]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(removedDataSet) removeDataSet(removedDataSet);
    }, [removedDataSet]); // eslint-disable-line react-hooks/exhaustive-deps


    return (
        <div ref={ref}>
            <img
                src={LayerControlIcon}
                alt='layers control'
                data-testid='map-layers-button'
                className='geonode-layers-control leaflet-touch leaflet-bar'
                onClick={() => setShowSideNav(true)}
            />
            <Drawer
                mask={false}
                onClose={() => setShowSideNav(false)}
                visible={showSideNav}
                data-testid='map-layers-panel'
                className="mapSideNav"
                getContainer={false}
                width={isMobile ? '100%' : 450}
                placement={isMobile ? 'bottom' : 'right'}
                height={isMobile ? '75vh' : undefined}
                bodyStyle={{ padding: 0, overflow: 'hidden' }}
                closable={false}
            >
                <div 
                    ref={drawerBodyRef}
                    className="map-layer-panel"
                >
                    {/* Sticky Header */}
                    <div className="map-layer-header">
                        <div className="map-layer-header-row">
                            <TopSection title={`MAP LAYERS`}/>
                            <span
                                className="map-layer-close"
                                onClick={() => setShowSideNav(false)}
                                title="Close"
                                role="button"
                                aria-label="Close layers panel"
                            >
                                <CloseOutlined />
                            </span>
                        </div>
                        <hr/>
                    </div>
                    
                    {/* Scrollable Content */}
                    <div className="map-layer-content">
                        <Spin spinning={false}>
                            <div className='DataSetsMenuItemDetails'>
                                {
                                    (Array.isArray(localLayers) && localLayers.length > 0) || layerCategories.length > 0 ? (
                                        <Collapse
                                            defaultActiveKey={[
                                                ...(Array.isArray(localLayers) && localLayers.length > 0 ? ['project-layers'] : []),
                                                ...(layerCategories.length > 0 ? [layerCategories[0].id] : []),
                                            ]}
                                            className="datasets-collapse"
                                        >
                                            {Array.isArray(localLayers) && localLayers.length > 0 && (
                                                <Panel header={`Project Layers`} key="project-layers" className="LayerCategories">
                                                    <Collapse bordered={false} expandIconPosition='end' ghost>
                                                        {localLayers.map((l) => (
                                                            <Panel
                                                                key={l.id}
                                                                header={<ProjectLayerItemHeader layer={l} />}
                                                                className="LayerCategories"
                                                            />
                                                        ))}
                                                    </Collapse>
                                                </Panel>
                                            )}
                                            {layerCategories.map((category) => (
                                                <Panel header={`${category.gn_description} (${category.count})`} key={category.id} className="LayerCategories">
                                                    <LayerCategory category={category} changeOpacity={changeOpacity} />
                                                </Panel>
                                            ))}
                                        </Collapse>
                                    ) : ''
                                }
                                
                            </div>
                        </Spin>
                    </div>
                </div>
            </Drawer>
        </div>
    );
}


const mapStateToProps = (state) => ({
    addedDataSet: mapDataSetsSelectors.getAddedDataSetSelector(state),
    removedDataSet: mapDataSetsSelectors.getRemovedDataSetSelector(state)
});

const mapDispatchToProps = (dispatch) => ({
    removeDataLayer: bindActionCreators(mapDataSetsActions.removeSelectedLayer, dispatch),
    addDataLayer: bindActionCreators(mapDataSetsActions.setSelectedLayer, dispatch),
});



export default connect(mapStateToProps, mapDispatchToProps)(LayerControl);

LayerControl.propTypes = {
    addedDataSet: PropTypes.object,
    removedDataSet: PropTypes.object,
    addDataLayer: PropTypes.func.isRequired,
    removeDataLayer: PropTypes.func.isRequired,
    localLayers: PropTypes.array,
}

LayerControl.defaultProps = {
    addedDataSet: null,
    removedDataSet: null,
    localLayers: [],
}
